import { FGStack, FGToast } from '@fg/design-system';
import FeedEmptyState from 'components/FeedEmptyState';
import { GQTabsNew } from '../GQTabs/GQTabs';
import React, {
    useCallback,
    useMemo,
    useRef,
} from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList as List } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import { EVENTS_FILTER, IEventsData, SCORED_EVENTS_FILTER } from './EventsFeedInterfaces';
import { isEmpty, keyBy, size } from 'lodash/fp';
import { IActiveCountry, IActiveDimension } from 'interfaces';
import GQLoader from 'components/GQLoader/GQLoader';
import FeedHeader from './FeedHeader';
import useWindowResize from 'hooks/useWindowResize';
import rootStore from '../../RootStore';
import EventsFeedRow from './EventsFeedRow';
import APIService from '../../services/APIService';
import useIsGQInternalUser from 'hooks/useIsGQInternalUser';
import moment from 'moment';

const STATUS_TABS = [
    { id: EVENTS_FILTER.ALL, label: 'All' },
    { id: EVENTS_FILTER.SCORED, label: 'Scored' },
    { id: EVENTS_FILTER.UNSCORED, label: 'Pending' },
];

const SCORING_TABS = [
    { id: SCORED_EVENTS_FILTER.ALL, label: 'All' },
    { id: SCORED_EVENTS_FILTER.HUMAN, label: 'Human' },
    { id: SCORED_EVENTS_FILTER.MACHINE, label: 'Machine' },
];

const IS_HIDE_TABS_FOR_NON_GQ_USERS = true;

type Props = {
    events: IEventsData[];
    onEventClick: (eventID: number) => void;
    loadMoreEvents: () => void;
    isLoading: boolean;
    loadFailed?: boolean;
    activeEventID: number;
    onEventDelete: (id: number) => void;
    canDeleteEvents: boolean;
    dimensions: { [dimensionId: number]: IActiveDimension };
    includeMentionCountries: boolean;
    licensedCountriesArr: IActiveCountry[];
    impactFilter?: number[];
    maxDate?: moment.Moment;
    minDate?: moment.Moment;
    selectedCountries?: number[];
    searchString?: string;
    feedFilter?: EVENTS_FILTER;
    scoredEventsFilter?: SCORED_EVENTS_FILTER;
    isShowCountryFilter?: boolean;
    isShowDateFilter?: boolean;
};

type RowItemData = {
    activeEventID: number;
};

//todo: refactor
const recurseParents = (
    element: HTMLDivElement,
    depth: number
): HTMLDivElement => {
    if (depth <= 2) {
        if (element.parentElement.id) {
            return element.parentElement as HTMLDivElement;
        } else {
            recurseParents(element.parentElement as HTMLDivElement, ++depth);
        }
    } else {
        return element;
    }
};

const EventsFeed = React.memo((props: Props) => {
    const [windowWidth] = useWindowResize();
    const isGQInternalUser = useIsGQInternalUser();
    const isShowTabs = !IS_HIDE_TABS_FOR_NON_GQ_USERS || isGQInternalUser;
    const feedIsEmpty = isEmpty(props.events);
    const isEventLoaded = useCallback(index => index < size(props.events), [
        props.events,
    ]);
    const sizeMap = useRef({});
    const setSize = useCallback((index, size) => {
        sizeMap.current = { ...sizeMap.current, [index]: size };
        if (listRef.current) {
            listRef.current.resetAfterIndex(index);
        }
    }, []);
    const getItemSize = useCallback(
        (index: number) => {
            let size = sizeMap.current[index] || 100;
            return size;
        },
        [props.events]
    );
    const getItemKey = useCallback(
        (index: number) => props.events[index]?.id ?? index,
        [props.events]
    );

    const countriesById: {
        [countryID: number]: IActiveCountry;
    } = useMemo(() => {
        return keyBy(
            (country: IActiveCountry) => country.id,
            props.licensedCountriesArr
        );
    }, [props.licensedCountriesArr]);

    const selectedStatusTab = useMemo(
        () => props.feedFilter || EVENTS_FILTER.ALL,
        [props.feedFilter]
    );

    const selectedScoringTab = useMemo(
        () => props.scoredEventsFilter || SCORED_EVENTS_FILTER.ALL,
        [props.scoredEventsFilter]
    );

    const onStatusTabChange = useCallback((id: string) => {
        rootStore.eventsFeedStore.setCurrentFilter(id as EVENTS_FILTER);
    }, []);

    const onScoringTabChange = useCallback((id: string) => {
        rootStore.eventsFeedStore.setScoredEventsFilter(id as SCORED_EVENTS_FILTER);
    }, []);

    const handleDelete = useCallback(async (id: number) => {
        try {
            await APIService.deleteEvent(id);
            props.onEventDelete(id);
            FGToast(
                { content: 'Event deleted successfully', type: 'neutral' },
                { autoClose: 3000, position: 'top-right' }
            );
        } catch (err) {
            console.error(err);
            FGToast(
                { content: `Failed to delete event #${id}`, type: 'error' },
                { autoClose: 5000, position: 'top-right' }
            );
        }
    }, [props.onEventDelete]);

    const handleDeleteAllClusterArticles = useCallback(
        async (eventId: number, clusterId: string, clusterDate: string) => {
            try {
                await APIService.deleteClusterArticles(clusterId, clusterDate);
                props.onEventDelete(eventId);
                FGToast(
                    {
                        content: 'Cluster articles deleted successfully',
                        type: 'neutral',
                    },
                    { autoClose: 3000, position: 'top-right' }
                );
            } catch (err) {
                console.error(err);
                FGToast(
                    {
                        content: `Failed to delete articles for cluster #${clusterId}`,
                        type: 'error',
                    },
                    { autoClose: 5000, position: 'top-right' }
                );
                throw err;
            }
        },
        [props.onEventDelete]
    );

    const handleDeleteAllClusterScores = useCallback(
        async (clusterId: string) => {
            try {
                await APIService.deleteClusterScores(clusterId);
                FGToast(
                    {
                        content: 'Machine scores deleted successfully',
                        type: 'neutral',
                    },
                    { autoClose: 3000, position: 'top-right' }
                );
            } catch (err) {
                console.error(err);
                FGToast(
                    {
                        content: `Failed to delete machine scores for cluster #${clusterId}`,
                        type: 'error',
                    },
                    { autoClose: 5000, position: 'top-right' }
                );
                throw err;
            }
        },
        []
    );

    const itemData: RowItemData = {
        activeEventID: props.activeEventID,
    };

    const listRef = useRef<List>(null);

    const showScoringTabs = props.feedFilter === EVENTS_FILTER.SCORED;

    return (
        <FGStack
            style={{ height: '100%', width: '100%' }}
            direction="column"
            gap={4}>
            <FeedHeader
                isShowImpactFilter
                isShowCountryFilter={props.isShowCountryFilter}
                isShowDateFilter={props.isShowDateFilter}
                impactFilter={props.impactFilter}
                maxDate={props.maxDate}
                minDate={props.minDate}
                selectedCountries={props.selectedCountries}
                searchString={props.searchString}
                onSetSearchString={str =>
                    rootStore.eventsFeedStore.setSearchString(str)
                }
                removeCountry={countryId =>
                    rootStore.eventsFeedStore.removeCountry(countryId)
                }
                addCountry={countryId =>
                    rootStore.eventsFeedStore.addCountry(countryId)
                }
                toggleCountry={countryId =>
                    rootStore.eventsFeedStore.toggleCountry(countryId)
                }
                onDateRangeChange={(startDate, endDate) => {
                    rootStore.eventsFeedStore.minDate = startDate
                        ? moment(startDate)
                        : null;
                    rootStore.eventsFeedStore.maxDate = endDate
                        ? moment(endDate)
                        : null;
                }}
                onDateRangeReset={() =>
                    rootStore.eventsFeedStore.resetDateFilter()
                }
            />
            {isShowTabs && (
                <>
                    <GQTabsNew
                        tabs={STATUS_TABS}
                        onChange={onStatusTabChange}
                        selectedTab={selectedStatusTab}
                    />
                    {showScoringTabs && (
                        <GQTabsNew
                            tabs={SCORING_TABS}
                            onChange={onScoringTabChange}
                            selectedTab={selectedScoringTab}
                        />
                    )}
                </>
            )}
            <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
                <div style={{ height: '100%', width: '100%', minHeight: 0, overflow: 'hidden' }}>
                    <AutoSizer>
                        {({ height, width }) => (
                            <InfiniteLoader
                                itemCount={size(props.events) + 1}
                                isItemLoaded={isEventLoaded}
                                loadMoreItems={props.loadMoreEvents}>
                                {({ onItemsRendered, ref }) => {
                                    return (
                                        <List
                                            height={height}
                                            width={width}
                                            style={{ overflowY: 'scroll' }}
                                            itemCount={size(props.events) + 1}
                                            itemSize={getItemSize}
                                            itemKey={getItemKey}
                                            onItemsRendered={onItemsRendered}
                                            estimatedItemSize={200}
                                            ref={list => {
                                                // this is not a normal React ref -
                                                // https://github.com/bvaughn/react-window/issues/324#issuecomment-528887341
                                                ref(list); // Give InfiniteLoader a reference to the list
                                                listRef.current = list; // Set your own ref to it as well.
                                            }}
                                            itemData={itemData}>
                                            {({ data, index, style }) => {
                                                return (
                                                    <div style={style}>
                                                        <EventsFeedRow
                                                            data={data}
                                                            index={index}
                                                            setSize={setSize}
                                                            {...props}
                                                            onEventDelete={
                                                                handleDelete
                                                            }
                                                            windowWidth={
                                                                windowWidth
                                                            }
                                                            isEventLoaded={
                                                                isEventLoaded
                                                            }
                                                            onEventClick={
                                                                props.onEventClick
                                                            }
                                                            countriesById={
                                                                countriesById
                                                            }
                                                            isGQInternalUser={
                                                                isGQInternalUser
                                                            }
                                                            onDeleteAllClusterArticles={
                                                                handleDeleteAllClusterArticles
                                                            }
                                                            onDeleteAllClusterScores={
                                                                handleDeleteAllClusterScores
                                                            }
                                                        />
                                                    </div>
                                                );
                                            }}
                                        </List>
                                    );
                                }}
                            </InfiniteLoader>
                        )}
                    </AutoSizer>
                </div>
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: feedIsEmpty ? 'auto' : 'none',
                    }}>
                    <FeedEmptyState
                        isLoading={props.isLoading}
                        loadFailed={props.loadFailed}
                        isEmpty={feedIsEmpty}
                        failedMessage="Couldn't load events"
                    />
                </div>
            </div>
            {props.isLoading && !feedIsEmpty && <GQLoader />}
        </FGStack>
    );
});

export default EventsFeed;

